import { useCallback, useEffect, useState } from 'react';
import { addCustodian, custodianList } from '../../api/EvidenceSourceUpload';
import { AddCustodianRequest, CustodianDto } from '../../types/upload-new-eviidence/evidenceSourceTypes';

export type ApiResult = {
  success: boolean;
  message: string;
};

interface UseCustodiansReturn {
  isLoading: boolean;
  custodians: CustodianDto[];
  addNewCustodian: (payload: AddCustodianRequest) => Promise<ApiResult>;
  fetchCustodians: () => Promise<void>;
}

export const useEvidenceCustodian = (
  clientId: any,
  projectId: any
): UseCustodiansReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [custodians, setCustodians] = useState<CustodianDto[]>([]);

  // ------------------------
  // Fetch custodians
  // ------------------------
  const fetchCustodians = useCallback(async () => {
    try {
      const response = await custodianList(clientId, projectId);

      if (response?.data?.meta?.status === 'Success') {
        setCustodians(response.data.data ?? []);
      }
    } catch (error) {
      console.error('fetchCustodians failed', error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, projectId]);

  // ------------------------
  // Add new custodian
  // ------------------------
  const addNewCustodian = useCallback(
    async (payload: AddCustodianRequest): Promise<ApiResult> => {
      setIsLoading(true);
      try {
        const response = await addCustodian(clientId, projectId, payload);

        if (response?.data?.meta?.status === 'Success') {
          await fetchCustodians();

          return {
            success: true,
            message:
              response.data.meta.message ||
              'Custodian added successfully',
          };
        }

        return {
          success: false,
          message:
            response?.data?.meta?.message || '',
        };
      } catch (error: any) {
        console.error('addNewCustodian failed', error);
        setIsLoading(false);
        return {
          success: false,
          message:
            error?.response?.data?.meta?.message ||
            '',
        };
      } finally {
        setIsLoading(false);
      }
    },
    [clientId, projectId, fetchCustodians]
  );

  useEffect(() => {
    fetchCustodians();
  }, [fetchCustodians]);

  return {
    isLoading,
    custodians,
    addNewCustodian,
    fetchCustodians,
  };
};
